import { useCallback } from "react";

import { Action, Dispatch } from "@reduxjs/toolkit";
import {
  formatDateActiveFilter,
  getAllDatesInRange,
} from "@src/features/table_filtering/utils/filter_utils";
import { ActiveFilter, ColumnConfig } from "@src/types";
import {
  ColDef,
  ColGroupDef,
  Column,
  GridApi,
  RowNode,
} from "ag-grid-community";
import { ColumnResizedEvent } from "ag-grid-community/dist/types/src/events";
import _ from "lodash";

/**
 * Will filter the table based on the quick filter input.
 *
 * @param {string} searchValue - The value in the quick filter input to filter the table.
 * @param {GridApi} gridApi - The API for interacting with the grid's data.
 * @param {function} filterCallBack (optional) - The function to call when the filter is applied.
 */
export function onChangeQuickFilter(
  searchValue: string,
  gridApi: GridApi,
  filterCallBack?: () => void
): void {
  gridApi.setGridOption("quickFilterText", searchValue);
  filterCallBack?.();
}

/**
 * Gathers active filters from the custom table filters and sets them as active filters.
 *
 * This function iterates through the custom table filters, and for each filter that has values,
 * it creates an array of `ActiveFilter` objects representing each filter type and value. It then
 * sets these active filters using the `setActiveFilters` function.
 *
 * @param {Record<string, string[] | boolean>} customTableFilters - All current custom table filters and their values.
 * @param {string[]} dateFilter (optional) - Current date filter values.
 */
export function gatherActiveFilters(
  customTableFilters: Record<string, string[] | boolean>,
  customDateFilters?: Record<string, string[] | boolean>,
  gridApi?: GridApi
): ActiveFilter[] {
  const activeFilters: ActiveFilter[] = [];

  for (const key of Object.keys(customTableFilters)) {
    const filterValue = customTableFilters[key];
    if (Array.isArray(filterValue)) {
      if (filterValue.length !== 0) {
        for (const value of filterValue) {
          activeFilters.push({
            filterType: key,
            filterValue: value,
          });
        }
      }
    } else if (typeof filterValue === "boolean") {
      if (filterValue) {
        const columnDef = gridApi?.getColumn(key).getColDef();
        const displayName = columnDef?.headerName;
        activeFilters.push({
          displayName: displayName,
          filterType: key,
          filterValue: key,
        });
      }
    }
  }

  if (customDateFilters) {
    for (const key of Object.keys(customDateFilters)) {
      const dateFilter = customDateFilters[key];

      if (dateFilter && typeof dateFilter !== "boolean") {
        const filterValue = formatDateActiveFilter(dateFilter);
        const columnDef = gridApi?.getColumn(key).getColDef();
        const headerName = columnDef?.headerName;

        activeFilters.push({
          displayName: `${headerName}: ${filterValue}`,
          filterType: key,
          filterValue: filterValue,
        });
      }
    }
  }

  return activeFilters;
}

/**
 * Change column display values, controlling the visibility of columns.
 *
 * @param {string[]} columnNames - The column headerNames of AG Grid to be displayed.
 * @param {GridApi} gridApi - The API for interacting with the grid's data.
 * @param {ColDef[]} columnDefs - The column definitions of AG Grid.
 */
export function onColumnVisibilityChanged(
  columnNames: string[],
  gridApi: GridApi,
  columnDefs: (ColDef | ColGroupDef)[]
): void {
  if (columnNames.length === 0) return;

  columnDefs.forEach((colDef) => {
    // show or hide column
    if (colDef["field"]) {
      gridApi.setColumnsVisible(
        [colDef["field"]],
        columnNames.includes(colDef.headerName)
      );
    }
    // if it's allocation, all its children should be shown or hidden
    if (colDef["groupId"] === "allocation") {
      const childrenColIds: string[] = colDef["children"].map(
        (childColDef: ColDef) => childColDef["colId"]
      );
      gridApi.setColumnsVisible(
        childrenColIds,
        columnNames.includes(colDef.headerName)
      );
    }
  });
}

/**
 * Change filter values for the dropdown filter and action required filter.
 *
 * @param {string[]} selectedElements - Selected elements to be filtered (filter values, eg. countries, regions, technologies).
 * @param {string} field - The field of the Ag Grid colDef to filter on.
 * @param {GridApi} gridApi - The API for interacting with the grid's data.
 * @param {function} filterCallBack (optional) - The function to call when the filter is applied.
 */
export function onFilterChange(
  selectedElements: string[],
  field: string,
  gridApi: GridApi,
  filterCallBack?: () => void
): void {
  if (!selectedElements || !selectedElements.length) {
    gridApi.setColumnFilterModel(field, null);
  } else {
    gridApi.setColumnFilterModel(field, {
      values: selectedElements,
    });
  }
  gridApi.onFilterChanged();
  filterCallBack?.();
}

/**
 * Update the table based on the selected column configs.
 *
 * @param {ColumnConfig[]} columnConfigs - The column configs to be applied.
 * @param {GridApi} gridApi - The API for interacting with the grid's data.
 */
export function onColumnConfigChange(
  columnConfigs: ColumnConfig[],
  gridApi: GridApi
) {
  const columnsByKey: Map<string, Column> = _.keyBy(
    gridApi.getColumns(),
    (col: Column) => col.getColDef().headerName
  );

  applyColumnWidthChange(columnConfigs, columnsByKey, gridApi);
}

/**
 * Apply the column width change to the grid.
 *
 * @param {ColumnConfig[]} columnConfigs - The column configs to be applied.
 * @param {Map<string, Column>} columnsByKey - The column objects to be applied.
 * @param {GridApi} gridApi - The API for interacting with the grid's data.
 */
function applyColumnWidthChange(
  columnConfigs: ColumnConfig[],
  columnsByKey: Map<string, Column>,
  gridApi: GridApi
) {
  if (!columnConfigs) return;
  const changedColumnWidths = columnConfigs?.filter(
    (columnConfig: ColumnConfig) => {
      const column = columnsByKey[columnConfig.key];
      return column && column.getActualWidth() !== columnConfig.width;
    }
  );

  gridApi.setColumnWidths(
    changedColumnWidths.map((column: ColumnConfig) => ({
      key: columnsByKey[column.key],
      newWidth: column.width,
    }))
  );
}

/**
 * Update the table based on the selected date range of the workload start date.
 *
 * @param {string[]} dateRange - Start and end date of the range for workload start date.
 * @param {string} field - The field of the Ag Grid colDef to filter on.
 * @param {GridApi} gridApi - The API for interacting with the grid's data.
 * @param {function} filterCallBack (optional) - The function to call when the filter is applied.
 */
export function onRangePickerChange(
  dateRange: string[] | null,
  field: string,
  gridApi: GridApi,
  filterCallBack?: () => void
): void {
  if (!dateRange || dateRange.length !== 2) {
    onFilterChange(null, field, gridApi, filterCallBack);
    return;
  }

  const [startDate, endDate] = dateRange;
  const dates: string[] = [];
  gridApi.forEachNode((rowNode: RowNode) => {
    dates.push(rowNode.data[field]);
  });
  const result = getAllDatesInRange(startDate, endDate, dates);

  applyDateRangeFilter(result, field, gridApi, filterCallBack);
}

/**
 * Apply filter based on date range result.
 *
 * @param {Object} result - The result from getAllDatesInRange.
 * @param {string} filterType - The type of filter being applied.
 * @param {GridApi} gridApi - The API for interacting with the grid's data.
 * @param {function} filterCallBack (optional) - The function to call when the filter is applied.
 */
function applyDateRangeFilter(
  result: { filtered: boolean; dates: string[] },
  filterType: string,
  gridApi: GridApi,
  filterCallBack?: () => void
): void {
  if (!result.filtered) return;

  if (!result.dates) {
    gridApi.setColumnFilterModel(filterType, null);
  } else {
    gridApi.setColumnFilterModel(filterType, {
      values: result.dates,
    });
  }
  gridApi.onFilterChanged();
  filterCallBack?.();
}

/**
 * Resets all active filters and sets the visibility of columns based on the current tab configuration.
 *
 * @param {GridApi} gridApi the API for interacting with the grid's data.
 */
export function resetAllActiveFilters(gridApi: GridApi): void {
  gridApi.setFilterModel(null);
}

/**
 * Handles column resize events in the grid and updates the column configurations.
 * The function is debounced to prevent excessive updates during continuous resizing.
 *
 * @param {Dispatch} dispatch - The dispatch function for updating the state.
 * @param {function} setColumnConfigs - The function to set the updated column configurations.
 * @param {ColumnConfig[]} currentColumnConfigs - The current list of column configurations.
 */
export function handleColumnResize(
  dispatch: Dispatch,
  setColumnConfigs: (columnConfigs: ColumnConfig[]) => Action,
  currentColumnConfigs: ColumnConfig[]
) {
  // Return the debounced handler for use in the component
  return useCallback(
    _.debounce((event: ColumnResizedEvent<any>) => {
      if (!event.column) return;

      const changedColumn: Column = event.column;
      const changedColumnConfig: ColumnConfig = {
        key: changedColumn.getColDef().headerName,
        width: changedColumn.getActualWidth(),
      };

      // Dispatch the updated column configurations.
      dispatch(
        setColumnConfigs(
          _.uniqBy([changedColumnConfig, ...currentColumnConfigs], "key")
        )
      );
    }, 1000),
    [dispatch, setColumnConfigs, currentColumnConfigs]
  );
}

/**
 * Clears a specific custom table filter property, handling both boolean and array filters.
 *
 * @param customTableFilters Current custom table filters.
 * @param key The key of the filter to clear.
 * @param value The value to clear (only used for array filters).
 * @returns Updated filters with the specified property cleared.
 */
export function clearCustomTableFilters<
  T extends Record<string, boolean | string[]>
>(customTableFilters: T, key: keyof T, value: string): T {
  const currentFilter = customTableFilters[key];

  let updatedFilter: boolean | string[];
  if (Array.isArray(currentFilter)) {
    updatedFilter = currentFilter.filter((item: string) => item !== value);
  } else if (typeof currentFilter === "boolean") {
    updatedFilter = false;
  } else {
    updatedFilter = currentFilter;
  }

  return {
    ...customTableFilters,
    [key]: updatedFilter,
  };
}
